import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { propertyApi } from '../../services/api';

const PropertyForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [property, setProperty] = useState(null);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  
  // Determine if we're editing an existing property
  const isEditing = Boolean(id);
  
  // Fetch property data if editing
  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      propertyApi.getById(id)
        .then(response => {
          setProperty(response.data);
          reset(response.data);
        })
        .catch(error => {
          toast.error('Failed to load property');
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, isEditing, reset]);
  
  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEditing) {
        await propertyApi.update(id, data);
        toast.success('Property updated successfully');
      } else {
        await propertyApi.create(data);
        toast.success('Property created successfully');
      }
      navigate('/properties');
    } catch (error) {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} property`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="card max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Property' : 'Add New Property'}
      </h1>
      
      {loading && !property && isEditing ? (
        <div className="text-center py-4">Loading property data...</div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-6">
            <div>
              <label htmlFor="name" className="form-label">Property Name</label>
              <input
                id="name"
                type="text"
                className="form-input"
                placeholder="e.g., Maple Avenue Apartments"
                {...register('name', { required: 'Property name is required' })}
              />
              {errors.name && <p className="form-error">{errors.name.message}</p>}
            </div>
            
            <div>
              <label htmlFor="address" className="form-label">Address</label>
              <input
                id="address"
                type="text"
                className="form-input"
                placeholder="Street address"
                {...register('address', { required: 'Address is required' })}
              />
              {errors.address && <p className="form-error">{errors.address.message}</p>}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="form-label">City</label>
                <input
                  id="city"
                  type="text"
                  className="form-input"
                  placeholder="City"
                  {...register('city', { required: 'City is required' })}
                />
                {errors.city && <p className="form-error">{errors.city.message}</p>}
              </div>
              
              <div>
                <label htmlFor="postalCode" className="form-label">Postal Code</label>
                <input
                  id="postalCode"
                  type="text"
                  className="form-input"
                  placeholder="Postal/Zip Code"
                  {...register('postalCode', { required: 'Postal code is required' })}
                />
                {errors.postalCode && <p className="form-error">{errors.postalCode.message}</p>}
              </div>
            </div>
            
            <div>
              <label htmlFor="country" className="form-label">Country</label>
              <input
                id="country"
                type="text"
                className="form-input"
                placeholder="Country"
                {...register('country', { required: 'Country is required' })}
              />
              {errors.country && <p className="form-error">{errors.country.message}</p>}
            </div>
            
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/properties')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditing ? 'Update Property' : 'Add Property'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default PropertyForm;